import type { OrderDashboardIssue } from '@/api/hooks/useOrderDashboard'
import type { SagaType } from '@/constants/sagaTypes'

/**
 * The metric grids double as filters for the issue list beside them: clicking
 * a partner row or an event row narrows the list to that partner or that
 * event. Both filters are independent and combine, the same way the scope
 * toggle and the count cards already do.
 */

export interface PartnerFilter {
  tenantId: string
  /** Carried along so the filter can name itself without the metrics report. */
  tenantName: string
}

export interface MetricFilters {
  partner: PartnerFilter | null
  /** A checkpoint label from the event metrics grid. */
  event: string | null
}

export const NO_METRIC_FILTERS: MetricFilters = { partner: null, event: null }

/**
 * Which sagas sit behind each event row.
 *
 * The event grid's rows are the order integration checkpoints, and the backend
 * fills each one from the latest log of a given saga type (see
 * `build_order_integration_checkpoints` and the `defs` map in
 * `app/dashboard/endpoints.py`) -- an issue only knows its own saga, so that
 * mapping is what turns an event row back into a set of issues. The labels are
 * the backend's; a row whose label is not here is left unclickable rather than
 * filtering to something it does not mean.
 *
 * `Completed` and `Status Updates` deliberately share a saga: completion is a
 * status update whose message says `(complete)`, and a failure inside that
 * saga usually never gets far enough to say so, so the two rows filter to the
 * same issues rather than one of them filtering to almost nothing.
 */
const CHECKPOINT_SAGA_TYPES: Record<string, SagaType[]> = {
  'Order Creation': ['create_order'],
  'Order Updates': ['update_order'],
  BOLs: ['progress_bol'],
  Drops: ['progress_drop'],
  Invoices: ['add_invoice'],
  Cancel: ['cancel_order'],
  Reset: ['reset_order'],
  Completed: ['progress_status'],
  'BOL Images': ['progress_bol_image'],
  'Status Updates': ['progress_status'],
}

/** Whether an event row can be turned into an issue filter at all. */
export function isFilterableEvent(label: string | null | undefined): boolean {
  return !!label && label in CHECKPOINT_SAGA_TYPES
}

export function hasMetricFilters(filters: MetricFilters): boolean {
  return !!filters.partner || !!filters.event
}

/**
 * A partner is whichever tenant is on the other end of the integration from
 * us, so an issue belongs to that partner if either end of its routing is
 * them -- which also covers a tenant integrating with itself.
 */
function matchesPartner(
  issue: OrderDashboardIssue,
  partner: PartnerFilter,
): boolean {
  const { origin_tenant_id, destination_tenant_id } = issue.routing
  return (
    origin_tenant_id === partner.tenantId ||
    destination_tenant_id === partner.tenantId
  )
}

function matchesEvent(issue: OrderDashboardIssue, event: string): boolean {
  const sagaTypes = CHECKPOINT_SAGA_TYPES[event]
  // An unmapped label filters nothing: the rows that produce one are not
  // clickable, so this only fires if a label is renamed backend-side.
  if (!sagaTypes) return true
  return sagaTypes.some((sagaType) => issue.saga_type === sagaType)
}

/** Narrows a window's issues to the picked partner and/or event row. */
export function filterIssuesByMetrics(
  issues: OrderDashboardIssue[] | undefined,
  filters: MetricFilters,
): OrderDashboardIssue[] {
  const all = issues ?? []
  const { partner, event } = filters
  if (!partner && !event) return all

  return all.filter(
    (issue) =>
      (!partner || matchesPartner(issue, partner)) &&
      (!event || matchesEvent(issue, event)),
  )
}
